"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Invoice, Customer, Vehicle, Appointment } from "@/types";

interface InvoiceDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  customers: Customer[];
  vehicles: Vehicle[];
  appointments?: Appointment[];
  getStatusBadge: (status: string) => React.JSX.Element;
  onDownload: (invoice: Invoice) => void;
}

export function InvoiceDetailDialog({
  open,
  onOpenChange,
  invoice,
  customers,
  vehicles,
  appointments = [],
  getStatusBadge,
  onDownload,
}: InvoiceDetailDialogProps) {
  if (!invoice) return null;

  const customer = customers.find((c) => c.id === invoice.customerId);

  // Find associated appointment to check for multiple vehicles
  const appointment = invoice.appointmentId
    ? appointments.find((a) => a.id === invoice.appointmentId)
    : null;

  // Resolve vehicles: preferably from appointment (supports multiple), fallback to invoice's single vehicle
  const invoiceVehicles: Vehicle[] = [];

  if (
    appointment &&
    appointment.vehicleIds &&
    appointment.vehicleIds.length > 0
  ) {
    // Get all vehicles from the appointment
    appointment.vehicleIds.forEach((vId) => {
      const v = vehicles.find((veh) => veh.id === vId);
      if (v) invoiceVehicles.push(v);
    });
  }

  // If no vehicles found from appointment (or no appointment), fall back to single invoice vehicle
  if (invoiceVehicles.length === 0 && invoice.vehicleId) {
    const v = vehicles.find((v) => v.id === invoice.vehicleId);
    if (v) invoiceVehicles.push(v);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Invoice Details</DialogTitle>
          <DialogDescription>{invoice.invoiceNumber}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Info */}
          <div className="flex justify-between items-start border-b pb-4">
            <div>
              <h3 className="text-lg font-bold">WeClean Car Wash</h3>
              <p className="text-sm text-muted-foreground">We come to you</p>
              <p className="text-sm text-muted-foreground">+974 7090 8373</p>
              <p className="text-sm text-muted-foreground">
                www.weclean.com.qa
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Invoice Date</p>
              <p className="font-medium">{formatDate(invoice.date)}</p>
              <p className="text-sm text-muted-foreground mt-2">Invoice #</p>
              <p className="font-medium">{invoice.invoiceNumber}</p>
            </div>
          </div>

          {/* Bill To */}
          <div>
            <Label className="text-muted-foreground">Bill To:</Label>
            <div className="mt-2">
              <p className="font-medium">{customer?.name || "N/A"}</p>
              <p className="text-sm text-muted-foreground">
                {customer?.phone || ""}
              </p>
              <p className="text-sm text-muted-foreground">
                {customer?.email || ""}
              </p>
            </div>
          </div>

          {/* Vehicle Info */}
          <div>
            <Label className="text-muted-foreground">
              Vehicle{invoiceVehicles.length > 1 ? "s" : ""}:
            </Label>
            <div className="mt-1 space-y-1">
              {invoiceVehicles.length > 0 ? (
                invoiceVehicles.map((v) => (
                  <p key={v.id}>
                    {v.make} {v.model} - {v.plateNumber}
                  </p>
                ))
              ) : (
                <p>N/A</p>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.items.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-center">
                      {item.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.unitPrice)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="border-t pt-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                {/* Backend discount is inverted: positive reduces the price,
                    negative is a surcharge. Both must be shown or the line
                    items will not add up to the total. */}
                {invoice.discount !== 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {invoice.discount < 0
                        ? "Price Adjustment (Surcharge):"
                        : "Price Adjustment (Discount):"}
                    </span>
                    <span
                      className={
                        invoice.discount < 0
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-emerald-600 dark:text-emerald-400"
                      }
                    >
                      {invoice.discount < 0 ? "+" : "-"}
                      {formatCurrency(Math.abs(invoice.discount))}
                    </span>
                  </div>
                )}
                {invoice.tax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax:</span>
                    <span>{formatCurrency(invoice.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(invoice.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="flex justify-between items-center border-t pt-4">
            <div>
              <Label className="text-muted-foreground">Payment Method:</Label>
              <p className="mt-1 capitalize">{invoice.paymentMethod || "-"}</p>
            </div>
            <div className="text-right">
              <Label className="text-muted-foreground">Payment Status:</Label>
              <div className="mt-1">
                {getStatusBadge(invoice.paymentStatus)}
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border-t pt-4">
              <Label className="text-muted-foreground">Notes:</Label>
              <p className="text-sm mt-1">{invoice.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => onDownload(invoice)}>
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
